import type { MessageKey } from "./cs.js";

export const en = {
  "app.name": "mesketch",
  "lang.switch.label": "Language",
  "lang.switch.other": "CS",
  "lang.switch.aria": "CS, switch to Czech",
  "nav.home": "Home",
  "footer.by": "by SEL Institute",
  "footer.terms": "Terms of service",
  "footer.privacy": "Privacy policy",
  "legal.title": "Coming soon",
  "legal.body": "We are preparing this text with our lawyer. It will be here soon.",
  "notFound.title": "Nothing here",
  "notFound.body": "The link is not valid, or the page does not exist.",
  "error.generic": "Something went wrong. Please try again.",
  "error.offline": "You are offline. Your answers stay on this device and will be sent once you are back online.",
  "action.retry": "Try again",

  "home.title": "Understand how you work",
  "home.subtitle": "How you experience emotions, where your strengths lie and what your personality is like. And what it means for your relationships, work and decisions. Free and with no sign-up.",
  "home.free.title": "{count, plural, =0 {Free tests} one {# test, free} =2 {Two tests, free} =3 {Three tests, free} =4 {Four tests, free} other {# tests, free}}",
  "home.side.what": "What you get",
  "home.side.result": "See your result",
  "home.example.sketch": "<b>Sketch</b> · free",
  "home.example.portrait": "<b>Portrait</b> · {style}",
  "home.example.portraitAlt": "Example of a Portrait in {style}: a figure with a glow around the head and a flower in the chest.",
  "home.unsaved": "{count, plural, one {You have # result here that is not saved to My self.} other {You have # results here that are not saved to My self.}}",
  "home.free.lead": "Take them one at a time, in any order. Emotions is the quickest place to start.",
  "home.sample.labelMobile": "This is what a result looks like",
  "home.picture.eyebrow": "With every result",
  "home.deeper.title": "When you want to go deeper",
  "home.deeper.text": "For each test you can unlock a detailed interpretation, a development plan and the picture in other techniques, for example to print.",
  "home.side.metaPaid": "Paid test, {duration}.",
  "home.side.metaFree": "Free, {duration}.",
  "home.test.go": "Start",
  "home.unsavedAction": "Save",
  "sketch.portrait.layer.adds.land": "Adds the coloured fields and the path of stones to the picture.",
  "sketch.portrait.layer.adds.sky": "Adds the stars and the aurora to the picture.",
  "sketch.portrait.layer.adds.inner": "Adds the colour of the sky, the glow and the flower in the chest to the picture.",
  "sketch.portrait.layer.adds.base": "Adds the shape behind your head to the picture.",
  "sketch.portrait.layer.next": "Next step:",
  "sketch.portrait.visitorTail": "You get your own sketch with the result. {duration}, free.",
  "sketch.portrait.visitorTailPaid": "You get your own sketch with the result. {duration}.",
  "sketch.head.label": "{label} · result and sketch",
  "home.picture.text": "Shapes, colours and their placement are calculated from your answers. Each test adds one layer. You can share it, and others can read something about you in it.",
  "home.picture.title": "A picture no one else will have",
  "home.where.decisions.text": "When you go with your head and when with your gut.",
  "home.where.decisions.title": "Decisions",
  "home.where.emotions.text": "What you show and what you keep to yourself.",
  "home.where.emotions.title": "Emotions",
  "home.where.work.text": "What energises you and where things come easily.",
  "home.where.work.title": "Work",
  "home.where.relations.text": "Why you withdraw in an argument, or push harder instead.",
  "home.where.relations.title": "Relationships",
  "home.where.lead": "When you know how you react and why, you also understand better what happens between you and others.",
  "home.where.title": "Where it helps",
  "home.sample.bell": "Bell curve: the dot is left of most people, on the “inside” side.",
  "home.sample.text": "You usually process sadness, anger or disappointment inside and look calm on the outside. So others sometimes don't notice that something is bothering you until you tell them.",
  "home.sample.sentence": "You keep difficult feelings inside more than about 7 in 10 people.",
  "home.sample.scale": "Expressing unpleasant feelings",
  "home.sample.label": "Sample result",
  "home.seoTitle": "free personality and emotional intelligence tests",
  "home.eyebrow": "Personality and emotional intelligence tests",
  "home.trust.text": "The questions come from IPIP, an open collection of scientific questionnaires used by psychologists around the world. We show the results honestly, including how precise they are. The app is backed by SEL Institute, the Institute for Social and Emotional Learning.",
  "home.layer.rounds": "{rounds, plural, one {in # round} other {in # rounds}}",
  "home.start.meta": "{duration} · free",
  "home.start.fallback": "Start: {name}",
  "save.first.title": "Where should we save your Sketch?",
  "save.first.lead": "We will send you a sign-in link, no password. In My self you will then find this Sketch and your other tests, on any phone or computer.",
  "save.first.skip": "Not now, show me the Sketch",
  "home.empty": "Tests are on their way.",
  "home.test.soon": "soon",
  "home.test.done": "done",
  "home.test.doneOpen": "done · see your result",
  "home.test.inProgress": "in progress",

  "intro.rounds": "{rounds, plural, one {# round} other {# rounds}}",
  "intro.items": "{items, plural, one {# question} other {# questions}}",
  "intro.free": "The result is free, no sign-up needed.",
  "intro.sex": "We do not ask about your gender; you are compared with general norms.",
  "intro.basedOn": "What the test is based on",
  "intro.start": "Start",
  "intro.continue": "Continue",
  "intro.restart": "Start over",
  "intro.result": "See your result",
  "intro.doneOn": "You finished this test on {date}.",
  "intro.doneUndated": "You've finished this test.",
  "intro.again": "Take the test again",
  "intro.again.text": "A new run takes {duration}. The new result becomes your current one and changes your picture; the previous one stays {signed, select, yes {in My self} other {on this device}}.",
  "intro.again.yes": "Start again",
  "intro.again.back": "Back to your result",
  "intro.paidSoon": "This test will be paid. We are preparing it.",

  "test.round": "Round {round} of {rounds}",
  "test.progress": "{index} of {count}",
  "test.progressLabel": "Progress in the round",
  "test.back": "Back",
  "test.later": "Take a break",
  "test.answers": "Answer",
  "test.completing": "Your sketch is being drawn…",
  "test.offlineRound": "The round result will be computed once you are back online.",
  "test.sessionLost": "This session is not on this device. Please start the test again.",
  "test.pause.title": "Take a break",
  "test.pause.lead": "We will send you a link to continue right from here, even on another device.",
  "test.pause.email": "Email",
  "test.pause.privacy": "We use your email only to send the link to continue. mesketch does not store it; we pass it to the Ecomail service to deliver the message.",
  "test.pause.privacyLink": "Privacy",
  "test.pause.send": "Send the link",
  "test.pause.back": "Back to the test",
  "test.pause.noEmail": "Without email",
  "test.pause.local": "You can continue on this device any time; your answers are saved.",
  "test.pause.offline": "The link cannot be sent while offline. You can continue on this device any time.",
  "test.pause.sent": "The link is on its way. Check your inbox.",
  "test.pause.devLink": "The development server does not send email. Link to continue:",
  "test.pause.error": "The email could not be sent. Please try again in a moment.",
  "test.pause.limit": "A link is already on its way. You can send another one in a while.",
  "test.pause.invalid": "Please check the email address.",
  "test.otherDevice": "This test continues on another device.",
  "result.lowReliability": "This scale is less precise than the others; take the result more as a hint.",
  "intro.sexAsked": "At the start we will ask which group to compare you with, because the norms for this test are separate for women and men.",
  "respondent.title": "Who should we compare you with?",
  "respondent.lead": "The norms for this test are separate for women and for men. To compare your result as well as we can, choose which group we should compare you with.",
  "respondent.f": "With women",
  "respondent.m": "With men",
  "respondent.note": "We do not ask anything else. Your choice only selects the norms and cannot be changed later.",
  "intro.pause": "You can stop at any time with “Take a break” and continue later, even on another device.",
  "intro.pauseLocal": "You can stop at any time with “Take a break” and continue later on this device.",
  "claim.title": "Continue the test",
  "claim.lead": "This link opens your test in progress on this device. You will no longer be able to continue on the previous device.",
  "claim.continue": "Continue on this device",
  "claim.gone": "This link is no longer valid",
  "claim.goneLead": "The link works only once and expires after a while.",
  "claim.home": "Go to the home page",
  "mail.login.subject": "Sign in to mesketch",
  "mail.login.greeting": "Hi,",
  "mail.login.lead": "here is your link to sign in to mesketch. After opening it, click Sign in:",
  "mail.login.button": "Sign in",
  "mail.login.validity": "The link is valid for {minutes} minutes and can be used once.",
  "mail.login.ignore": "If you were not expecting this email, do nothing, the link will expire on its own.",
  "nav.me": "My self",
  "me.title": "My self",
  "me.lead": "See your results on other devices too. We use your email only to send you a sign-in link.",
  "me.email.label": "Email",
  "me.email.send": "Send link",
  "me.email.sent": "We sent you a link. Open it and click Sign in.",
  "me.email.devLink": "On the development version no email is sent, here is the link:",
  "me.email.failed": "We could not send the email. Please try again in a moment.",
  "me.rateLimited": "Too many attempts in a short time. Please try again in a moment.",
  "me.signedAs": "Signed in as {email}",
  "me.logout": "Sign out",
  "me.logoutAll": "Sign out on all devices",
  "me.tests": "Your tests",
  "me.inProgress": "In progress · round {round} of {rounds}",
  "me.continue": "Continue",
  "me.done": "Done {date}",
  "me.open": "Open",
  "me.history": "Earlier results",
  "me.newLink": "New link for sharing",
  "me.newLinkNote": "The old link will stop working.",
  "me.notStarted": "Not started yet",
  "me.start": "Start",
  "me.deviceTests": "Tests on this device",
  "me.soon.title": "Coming soon",
  "me.soon.portraits": "Purchased Portraits",
  "me.soon.pairs": "Pairs",
  "me.soon.codes": "Code balances",
  "me.soon.data": "Download and delete your data",
  "me.soon.note": "coming soon",
  "login.title": "Sign in",
  "login.lead": "Click the button to sign in on this device for 90 days.",
  "login.button": "Sign in",
  "login.gone": "This link is no longer valid. Request a new one on the My self page.",
  "login.goMe": "Go to My self",
  "mail.resume.subject": "Your link to continue the test",
  "mail.resume.greeting": "Hi,",
  "mail.resume.lead": "here is the link to continue your mesketch test where you took a break:",
  "mail.resume.button": "Continue the test",
  "mail.resume.validity": "The link is valid for {days} days and works once.",
  "mail.resume.ignore": "If you did not expect this email, just ignore it; the link expires on its own.",

  "preview.banner": "Preview",
  "preview.note": "Draft texts are highlighted, a missing text shows its key.",

  "round.done": "Round {round} of {rounds} done",
  "round.stage.ghost": "Outline",
  "round.stage.noun": "Main stroke",
  "round.stage.adjective": "Mood",
  "round.hint.race": "Your type is made of two words.",
  "round.hint.noun": "The main stroke is on paper, the word before it is still missing.",
  "round.hint.both": "Pencil may still change, ink holds.",
  "round.race.noun": "Which one are you? The race after round {round}",
  "round.race.adjective": "The race for the adjective",
  "round.certainty": "How sure we are so far",
  "round.word.drawing": "still being drawn",
  "round.continue": "Continue to round {round} · {items, plural, one {# question} other {# questions}}",
  "round.offline": "Offline preview. Once you are back online, the result from the server replaces it.",
  "round.domains": "{count, plural, =6 {Six dimensions} =7 {Seven dimensions} =8 {Eight dimensions} one {# dimension} other {# dimensions}}",

  "state.clear": "clear",
  "state.almost": "almost clear",
  "state.drawing": "being drawn",
  "bar.label": "{scale}: from the {low} pole to the {high} pole, {state}",
  "bar.labelFinal": "{scale}: from the {low} pole to the {high} pole",

  "sketch.title": "Your Sketch is ready",
  "sketch.titleVisitor": "Sketch",
  "sketch.lead": "The type and the dimensions are yours for free.",
  "sketch.leadNoType": "Your result in all dimensions is yours for free.",
  "sketch.type": "Your type",
  "sketch.typeVisitor": "Type",
  "sketch.secondary": "Second closest: {name} ({share}).",
  "sketch.certainty": "Type certainty {share}.",
  "sketch.domains": "{count, plural, =6 {Six dimensions} =7 {Seven dimensions} =8 {Eight dimensions} one {# dimension} other {# dimensions}}",
  "sketch.signature": "The trait that sets you apart",
  "sketch.freeChapter": "Free chapter",
  "sketch.locked": "More in the Portrait",
  "sketch.retry": "Take the test again",
  "sketch.again": "Take the test again",
  "sketch.fits.question": "Does it fit me?",
  "sketch.fits.fits": "fits",
  "sketch.fits.partly": "partly",
  "sketch.fits.doesnt_fit": "doesn't fit",
  "sketch.share.title": "Link to the Sketch",
  "sketch.share.note": "Anyone with the link can see the Sketch. You can revoke the link at any time.",
  "sketch.share.copy": "Copy link",
  "sketch.share.copied": "Copied",
  "sketch.share.revoke": "Revoke the link and create a new one",
  "sketch.share.revoked": "The old link no longer works.",
  "sketch.visitor.lead": "This is someone's result.",
  "sketch.visitor.cta": "Take the test",
  "sketch.visitor.own": "Get your own",
  "sketch.head": "Sketch · {count, plural, one {# scale} other {# scales}}",
  "sketch.save.title": "Save your result to My self",
  "sketch.save.lead": "We will send you a sign-in link. Then you will find this and your other results from this device in My self, on any phone or computer.",
  "sketch.summary.balanced": "Nothing stands out strongly. You lean depending on the situation and the people around you.",
  "sketch.summary.topTitle": "Your strongest",
  "sketch.summary.topWorkTitle": "What you would enjoy most",
  "sketch.summary.tie": "Sharing this place:",
  "sketch.summary.top": "Your strongest: {list}.",
  "sketch.summary.topWork": "What you would enjoy most: {list}.",
  "sketch.summary.topMore": "Your strongest: {list} and {count} more sharing place {place}.",
  "sketch.summary.topTie": "Your strongest: {count} sharing first place.",
  "sketch.summary.topWorkMore": "What you would enjoy most: {list} and {count} more sharing place {place}.",
  "sketch.summary.topWorkTie": "You would enjoy {count} areas equally.",
  "sketch.insight": "What's interesting about it",
  "sketch.prominent": "What stands out most about you",
  "sketch.leaning": "Where you lean most",
  "sketch.leans": "rather {pole}",
  "sketch.balanced": "Where it's balanced",
  "sketch.balancedNote": "Here you lean depending on the situation and the people around you.",
  "sketch.notComputed": "{name}: too few answers for an estimate.",
  "sketch.hint": "≈ hint",
  "sketch.legend.band": "the range where you most likely are",
  "sketch.legend.low": "dashed: this scale is less precise, take it as a hint",
  "sketch.legend.order": "The middle of the bar is your own average. The ranking is only within you, with no comparison to others.",
  "sketch.top.strengths": "Your strongest in detail",
  "sketch.top.work": "Where you fit best",
  "sketch.order.bottom": "What stays more in the background",
  "sketch.order.all": "The full ranking",
  "sketch.order.place": "{place}.",
  "sketch.dominant": "The virtue that leads for you",
  "sketch.dominantNote": "A virtue is a group of strengths, not a separately measured trait.",
  "sketch.order.low": "less pronounced for you",
  "sketch.order.high": "more pronounced for you",
  "sketch.order.label": "{scale}: from less pronounced for you to more pronounced for you",
  "sketch.stars.label": "Stars of your strongest strengths: {list}",
  "sketch.steps.label": "Path to your strongest areas: {list}",
  "sketch.portrait.title": "How do you work?",
  "sketch.portrait.captionLabelVisitor": "How the owner of the picture describes themselves",
  "sketch.portrait.captionBalanced": "Mostly I have both sides, it depends on the situation.",
  "sketch.portrait.none": "There are not enough answers for a picture yet.",
  "sketch.portrait.drawing": "The portrait is being drawn…",
  "sketch.portrait.send": "Send link",
  "sketch.portrait.sent": "Link copied",
  "sketch.portrait.inPicture": "In the picture: {element}",
  "sketch.portrait.picture.aura.neg": "a thin band of golden light around the head",
  "sketch.portrait.picture.aura.pos": "a large golden glow around the head",
  "sketch.portrait.picture.core_open.neg": "a deep night sky and a glowing bud in the chest",
  "sketch.portrait.picture.core_open.pos": "a red sunset and an open flower in the chest",
  "sketch.portrait.picture.density.neg": "a calm sky of long strokes",
  "sketch.portrait.picture.density.pos": "a sky full of swirls",
  "sketch.portrait.picture.head_mode.neg": "calm strokes along the outline of the head",
  "sketch.portrait.picture.head_mode.pos": "a whirl in the head",
  "sketch.portrait.picture.palette_head.neg": "a blue head",
  "sketch.portrait.picture.palette_head.pos": "a pink head",
  "sketch.portrait.picture.sparks.neg": "a sky without sparks",
  "sketch.portrait.picture.sparks.pos": "a sky full of sparks",
  "sketch.portrait.picture.reach.neg": "narrow shoulders without leaves",
  "sketch.portrait.picture.reach.pos": "broad shoulders and large leaves",
  "sketch.portrait.picture.reach_warmth.neg": "a turquoise figure",
  "sketch.portrait.picture.reach_warmth.pos": "a terracotta figure",
  "sketch.picture.drawing": "The picture is being painted…",
  "sketch.picture.failed": "The picture couldn't be loaded right now.",
  "sketch.picture.download": "Download the image",
  "sketch.picture.share": "Share the image",
  "sketch.picture.note": "You'll download your whole picture. The share link shows only this test's layer, and the link preview shows the picture.",
  "sketch.portrait.glance": "At a glance",
  "sketch.portrait.glance.prominent": "{count, plural, =1 {One thing sets you apart from most people.} =2 {Two things set you apart from most people.} =3 {Three things set you apart from most people.} =4 {Four things set you apart from most people.} =5 {Five things set you apart from most people.} =6 {Six things set you apart from most people.} =7 {Seven things set you apart from most people.} other {# things set you apart from most people.}}",
  "sketch.portrait.glance.balanced": "{count, plural, =1 {In one more you have both sides and it depends more on the situation.} =2 {In two more you have both sides and it depends more on the situation.} =3 {In three more you have both sides and it depends more on the situation.} =4 {In four more you have both sides and it depends more on the situation.} =5 {In five more you have both sides and it depends more on the situation.} =6 {In six more you have both sides and it depends more on the situation.} other {In # more you have both sides and it depends more on the situation.}}",
  "sketch.portrait.differ": "What sets you apart",
  "sketch.portrait.differ.note": "The curve shows where most people are. The dot is you, the coloured area is the range where you most likely are.",
  "sketch.portrait.signs": "How it may show",
  "sketch.portrait.question": "Something to think about",
  "sketch.portrait.both": "Where you have both sides",
  "sketch.portrait.both.note": "Here you don't lean to one side. It depends more on the situation and the people around you. That says something too: you can do both.",
  "sketch.portrait.middle": "You are in the middle, where most people are.",
  "sketch.portrait.share.approx": "about {n}",
  "sketch.portrait.share.exact": "{n}",
  "sketch.portrait.bell.most": "most people",
  "sketch.portrait.bell.left": "Your dot is to the left of most people, on the {pole} side.",
  "sketch.portrait.bell.right": "Your dot is to the right of most people, on the {pole} side.",
  "sketch.portrait.bell.middle": "Your dot is in the middle, where most people are.",
  "sketch.portrait.flower.label": "A flower with a petal for each scale. A longer petal is further from the middle. Prominent: {list}.",
  "sketch.portrait.flower.labelCalm": "A flower with a petal for each scale. All petals are close to the middle.",
  "sketch.portrait.flower.legend.mid": "middle of the scale",
  "sketch.portrait.flower.legend.prominent": "prominent",
  "sketch.portrait.flower.legend.low": "measured less precisely",
  "sketch.portrait.finish.title": "Finish your portrait",
  "sketch.portrait.finish.lead": "Your Sketch showed you where you are. The Portrait explains what it means for you and what you can do with it.",
  "sketch.portrait.finish.what": "What you get in the Portrait",
  "sketch.portrait.finish.item.depth": "A detailed interpretation of every scale: what your result means and how it shows in everyday situations.",
  "sketch.portrait.finish.item.links": "How your traits work together and what follows from them as a whole.",
  "sketch.portrait.finish.item.watch": "Where it may hold you back and what to watch out for.",
  "sketch.portrait.finish.item.plan": "A development plan: three concrete steps for the next month.",
  "sketch.portrait.finish.item.pdf": "The whole interpretation as a PDF you can come back to.",
  "portrait.page.label": "Portrait",
  "portrait.page.title": "Portrait · {test}",
  "portrait.page.lead": "Here is your personal portrait: a complete look at you, what your result means, how it shows up in life, how your traits meet, where they can hold you back and what you can do about it.",
  "portrait.page.whole": "Your complete portrait",
  "portrait.page.depth": "What your result means",
  "portrait.page.scenes": "How it shows up in your life",
  "portrait.page.scene_work": "At work and in decisions",
  "portrait.page.scene_relations": "In relationships",
  "portrait.page.scene_stress": "Under pressure",
  "portrait.page.connections": "How your traits meet",
  "portrait.page.weak": "Where it can hold you back",
  "portrait.page.practice": "Into practice",
  "portrait.page.practice_do": "What helps you",
  "portrait.page.practice_avoid": "What to watch out for",
  "portrait.page.plan": "Your plan for next month",
  "portrait.page.picture": "Your picture",
  "portrait.page.aiNote": "Written for you from your result.",
  "portrait.page.otherLanguage": "The Portrait is in the language you took the test in.",
  "portrait.page.drawing": "Your Portrait is being finished…",
  "portrait.page.drawingHint": "It usually takes under a minute. This page updates by itself, there's nothing you need to do.",
  "portrait.page.later": "We're finishing it, come back in a little while. You'll also find it in My self.",
  "portrait.page.failed": "We couldn't finish your Portrait just now. We know about it, please come back later.",
  "portrait.page.back": "Back to the Sketch",
  "sketch.portrait.finish.open": "Complete the portrait",
  "sketch.portrait.finish.show": "Open the Portrait",
  "sketch.portrait.finish.devNote": "Free on the development site, payment comes later.",
  "sketch.portrait.finish.unavailable": "The portrait can't be completed right now, please try again later.",
  "me.portrait": "Portrait",
  "sketch.portrait.style.watercolor": "watercolour",
  "sketch.portrait.style.cubism": "cubism",
  "sketch.portrait.style.impasto": "impasto",
  "sketch.portrait.style.ornament": "ornament",
  "sketch.portrait.style.geometry": "geometry",
  "sketch.portrait.style.riso": "risograph",
  "sketch.portrait.preview.alt": "Your portrait in {style}, a small preview",
  "sketch.portrait.preview.note": "Small previews of your portrait. You get the full picture once it is finished.",
  "sketch.portrait.layer.title": "Your portrait",
  "sketch.portrait.layer.count": "{done} of {total, plural, one {# layer} other {# layers}}",
  "sketch.portrait.layer.old": "This is an older result. This test's layer in the picture comes from it, the others from your latest tests.",
  "sketch.portrait.layer.new.base": "New: the shape behind your head.",
  "sketch.portrait.layer.new.inner": "New: the colour of the sky, the glow and the flower in the chest.",
  "sketch.portrait.layer.new.sky": "New: the stars and the aurora above your head.",
  "sketch.portrait.layer.new.land": "New: the coloured fields and the path of stones.",
  "sketch.portrait.layer.region": "Picture",
  "sketch.portrait.example.alt": "Example picture {n}: a shape behind the head, coloured fields with a path, stars above the head and a figure with a flower in the chest.",
  "sketch.portrait.layer.inner": "In the picture: {list}.",
  "sketch.portrait.layer.sky": "The stars above your head: your strongest strengths. The biggest star: {name}.",
  "sketch.portrait.layer.land": "The coloured fields and the stones on the path: the areas of work you enjoy. The golden stone: {name}.",
  "sketch.portrait.layer.base.backdrop_shape.neg": "An arch behind your head: your trust in what has proven itself.",
  "sketch.portrait.layer.base.backdrop_shape.pos": "Loose lobes and a contrasting colour behind your head: your openness to new things.",
  "sketch.portrait.layer.base.backdrop_layers.neg": "One calm ring behind your head: your need for quiet and space of your own.",
  "sketch.portrait.layer.base.backdrop_layers.pos": "Several rings behind your head: your energy among people.",
  "sketch.portrait.layer.base.backdrop_warmth.neg": "A lavender colour behind your head: you say things straight and stand your ground.",
  "sketch.portrait.layer.base.backdrop_warmth.pos": "A terracotta colour behind your head: your patience and kindness towards others.",
  "sketch.portrait.layer.base.backdrop_cut.neg": "Restless strokes in the shape behind your head: your freedom and planning as you go.",
  "sketch.portrait.layer.base.backdrop_cut.pos": "Even strokes along the outline of the shape: your care and sense of order.",
  "sketch.portrait.layer.base.backdrop_softness.neg": "A sharp edge to the shape: your composure, even under pressure.",
  "sketch.portrait.layer.base.backdrop_softness.pos": "An edge that melts into the sky: your sensitivity and need for closeness.",
  "sketch.portrait.layer.base.backdrop_ornament.neg": "Dots along the edge of the shape: your wish to stand out and your joy in beautiful things.",
  "sketch.portrait.layer.base.backdrop_ornament.pos": "A plain shape without dots: your modesty and fairness.",
  "sketch.share.layer": "Others will see only this test's layer, not your whole picture. The link preview shows this picture.",
  "sketch.visitor.time": "You can take yours for free, it takes {duration}.",
  "round.leading": "Leading so far",
  "round.leadingTie": "Sharing this place so far:",
  "round.image.note": "In pencil for now. It gets its colours on the Sketch after the last round.",
  "round.image.portrait": "Pencil portrait from your answers so far.",
  "round.leadingNote": "The ranking may still change.",
  "sketch.noLink": "The link to the Sketch was not kept on this device.",

  "og.result.description": "A sketch from the “{name}” test on mesketch.",
  "paywall.sketch": "Sketch",
  "paywall.sketchDesc": "type and dimensions",
  "paywall.have": "yours",
  "paywall.portrait": "Portrait",
  "paywall.portraitDesc": "all traits, interpretation and a plan",
  "paywall.price": "190 CZK",
  "paywall.button": "Complete the portrait · 190 CZK (coming soon)",
  "pairs.card.title": "How do you two fit?",
  "pairs.card.lead": "Invite a partner, friend or colleague. We'll put your two portraits side by side and show where you click and where there'll be friction.",
  "pairs.card.invite": "Invite to a pair",
  "pairs.form.inviter": "What do they call you?",
  "pairs.form.inviterHint": "They'll see you under this name.",
  "pairs.form.invitee": "Who are you inviting?",
  "pairs.form.inviteeHint": "A nickname is enough, no need for a full name.",
  "pairs.form.email": "Their email (optional)",
  "pairs.form.emailHint": "We'll send the invitation for you. We don't keep the address.",
  "pairs.form.submit": "Create invitation",
  "pairs.form.back": "Back",
  "pairs.form.invalidLabel": "A nickname can have at most 40 characters, with no email address or link.",
  "pairs.form.failed": "The invitation couldn't be created. Please try again.",
  "pairs.created.title": "The invitation is ready",
  "pairs.created.lead": "Send the link to {name}. It's valid until {date}.",
  "pairs.created.once": "You'll only see the link now. If you lose it, cancel the invitation and create a new one.",
  "pairs.created.emailSent": "We've also sent the invitation by email.",
  "pairs.created.copy": "Copy link",
  "pairs.created.copied": "Copied",
  "pairs.created.share": "Share link",
  "pairs.created.done": "Done",
  "pairs.list.title": "Your pairs",
  "pairs.state.waiting": "waiting to be accepted",
  "pairs.state.declined": "declined",
  "pairs.state.cancelled": "invitation cancelled",
  "pairs.state.expired": "invitation expired",
  "pairs.state.ready": "connected",
  "pairs.state.incomparable": "connected, results can't be compared",
  "pairs.state.same_person": "connected, both results belong to the same email",
  "pairs.state.unavailable": "connected, the pair result can't be prepared right now",
  "pairs.state.revoked": "connection cancelled",
  "pairs.open": "Open pair",
  "pairs.cancelInvite": "Cancel invitation",
  "pairs.revoke": "Cancel connection",
  "pairs.confirmNo": "Keep",
  "pairs.invite.title": "{name} invites you to a pair",
  "pairs.invite.test": "Test: {test}",
  "pairs.invite.lead": "You'll take the test and see your result. Then you decide whether to connect it with the result of {name}. You'll both see the connected results, and you can withdraw your consent at any time.",
  "pairs.invite.start": "Take the test",
  "pairs.invite.useMine": "Connect my result",
  "pairs.invite.loginForResult": "Have a result elsewhere? Sign in",
  "pairs.invite.decline": "Decline",
  "pairs.invite.declined": "The invitation is declined. Nothing else will happen.",
  "pairs.invite.privacy": "The nickname you appear under here was given to us by the person inviting you. We'll delete the invitation and the nickname 30 days after it expires or is declined.",
  "pairs.invite.privacyLink": "Privacy",
  "pairs.invite.gone.title": "This invitation is no longer valid",
  "pairs.invite.gone.lead": "The link has expired, has already been used, or the person who invited you cancelled it. Ask for a new invitation.",
  "pairs.invite.self": "This is your own invitation. Send the link to the person you're inviting.",
  "pairs.invite.mismatch": "This result can't be used for the pair. Take the test again.",
  "pairs.join.title": "Connect results: you and {name}",
  "pairs.join.yes": "Connect",
  "pairs.join.decline": "Decline",
  "pairs.join.later": "Not now",
  "pairs.join.login": "Sign in so you can cancel the connection from another device too.",
  "consent.pair_link.v1.invite": "Your latest result of this test goes into the pair. Once the invitation is accepted, you'll see each other's position on every scale of the test and each other's portraits, and you'll both see the pair result. You can withdraw your consent at any time, and the pair will then be hidden for both of you.",
  "consent.pair_link.v1.accept": "Your latest result of this test goes into the pair. You'll see each other's position on every scale of the test and each other's portraits, and you'll both see the pair result. You can withdraw your consent at any time, and the pair will then be hidden for both of you.",
  "consent.pair_link.v1.decline": "Decline the invitation? The person who invited you will see it was declined. Nothing else will happen.",
  "consent.pair_link.v1.revoke": "Cancel the connection? The pair result will be hidden and deleted for both of you. This can't be undone, a new connection needs a new invitation.",
  "consent.pair_link.v1.cancel": "Cancel the invitation? The link will stop working.",
  "pairs.page.title": "You and {name}",
  "pairs.page.you": "You",
  "pairs.page.portraits": "Your portraits",
  "pairs.page.harmony": "Where you click",
  "pairs.page.complement": "Where you complement each other",
  "pairs.page.friction": "Where there'll be friction",
  "pairs.page.shared_risk": "What to watch out for together",
  "pairs.page.tip": "Tip",
  "pairs.page.placeholder": "Sample rule",
  "pairs.page.none": "The rules found nothing striking for the two of you.",
  "pairs.page.scales": "Scale by scale",
  "pairs.page.meLeft": "You're more to the left, {name} more to the right.",
  "pairs.page.meRight": "You're more to the right, {name} more to the left.",
  "pairs.page.alike": "You're in a similar place.",
  "pairs.page.flower": "The flower of you both",
  "pairs.page.flowerLabel": "Two flowers on top of each other: yours and that of {name}.",
  "pairs.page.groupNote": "Each of you is compared with your own comparison group.",
  "pairs.page.incomparable": "Your results come from versions of the test that can't be compared.",
  "pairs.page.retake": "Take the test again.",
  "pairs.page.retakeLogin": "Sign in before the test so your new result counts in the pair too.",
  "pairs.page.waitOther": "We're waiting for {name} to take the test again.",
  "pairs.page.samePerson": "Both results now belong to the same email, so there's no pair result.",
  "pairs.page.unavailable": "The pair result can't be prepared right now.",
  "me.pairs": "Pairs",
  "me.pairs.none": "No pairs yet. You can invite someone from a test result.",
  "mail.pair_invite.subject": "{name} invites you to a pair on mesketch",
  "mail.pair_invite.greeting": "Hi,",
  "mail.pair_invite.lead": "{name} invites you to put your portraits side by side on mesketch and see where you click and where there'll be friction. Open the invitation here:",
  "mail.pair_invite.button": "Open invitation",
  "mail.pair_invite.validity": "The invitation is valid for {days} days.",
  "mail.pair_invite.ignore": "If you're not interested, do nothing, it will expire on its own.",
  "mail.pair_invite.privacy": "Your address was given to us by the person inviting you, only for this invitation. We don't keep it, the email is sent by Ecomail, our processor.",
} as const satisfies Record<MessageKey, string>;
